import React, { useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Alert, Box, Grid } from '@mui/material';
import { AgentActivityLogReviewStatus } from '@trnsact/trnsact-shared-types';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { buildFieldEditGroups } from './lib/fieldGroups';
import { ExtractedFieldsPanel } from './components/ExtractedFieldsPanel';
import { OriginalDocumentPanel } from './components/OriginalDocumentPanel';
import { ReviewActionsBar } from './components/ReviewActionsBar';
import { ReviewHeader } from './components/ReviewHeader';
import {
  AGENT_ACTIVITY_LOG_REVIEW_CONFIRM,
  AGENT_ACTIVITY_LOG_REVIEW_REJECT,
  ConfirmAgentActivityLogReviewResponse,
  RejectAgentActivityLogReviewResponse,
} from './queries';
import { ExtractionRecord, LenderReviewLocationState, PayloadAttachment } from './types';

export const LenderApplicationReviewPage = () => {
  const history = useHistory();
  const { activityId } = useParams<{ activityId: string }>();
  const location = useLocation<LenderReviewLocationState | undefined>();
  const activity = location.state?.activity;

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmReview, { loading: confirming }] = useMutation<ConfirmAgentActivityLogReviewResponse>(
    AGENT_ACTIVITY_LOG_REVIEW_CONFIRM
  );
  const [rejectReview, { loading: rejecting }] = useMutation<RejectAgentActivityLogReviewResponse>(
    AGENT_ACTIVITY_LOG_REVIEW_REJECT
  );
  const submitting = confirming || rejecting;

  const extraction = (activity?.payload?.detailed_extraction as ExtractionRecord | null | undefined) ?? null;
  const attachments = Array.isArray(activity?.payload?.attachments)
    ? (activity!.payload!.attachments as PayloadAttachment[])
    : [];
  const overallConfidence = (activity?.trigger?.orchestrator_confidence_score as number | undefined) ?? null;

  const groups = useMemo(() => buildFieldEditGroups(extraction, edits), [extraction, edits]);
  const editedCount = useMemo(
    () => groups.reduce((count, group) => count + group.fields.filter(field => field.isDirty).length, 0),
    [groups]
  );

  const handleChange = (key: string, value: string) => setEdits(prev => ({ ...prev, [key]: value }));

  const handleRevert = (key: string) =>
    setEdits(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const handleSubmit = async (status: AgentActivityLogReviewStatus) => {
    if (!activityId) return;
    setSubmitError(null);
    try {
      const response =
        status === AgentActivityLogReviewStatus.Confirmed
          ? await confirmReview({
              variables: {
                input: {
                  agentActivityLogId: activityId,
                  fieldEdits: Object.entries(edits).map(([key, value]) => ({ key, value })),
                },
              },
            })
          : await rejectReview({ variables: { input: { agentActivityLogId: activityId } } });

      const result =
        status === AgentActivityLogReviewStatus.Confirmed
          ? (response.data as ConfirmAgentActivityLogReviewResponse | undefined)?.agentActivityLogReview.confirm
          : (response.data as RejectAgentActivityLogReviewResponse | undefined)?.agentActivityLogReview.reject;

      if (result && !result.success) {
        setSubmitError(result.error ?? 'Something went wrong while saving this review.');
        return;
      }

      history.push('/assistant-log');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong while saving this review.');
    }
  };

  if (!activity) {
    return (
      <PageContainer title="Review Extracted Terms">
        <Box sx={{ p: 3 }}>
          <Alert severity="info">Open this screen from an activity in the Assistant Log.</Alert>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Review Extracted Terms">
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <ReviewHeader overallConfidence={overallConfidence} />

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ExtractedFieldsPanel groups={groups} onChange={handleChange} onRevert={handleRevert} />
          </Grid>
          <Grid item xs={12} md={6}>
            <OriginalDocumentPanel agentActivityLogId={activityId} attachments={attachments} />
          </Grid>
        </Grid>

        <ReviewActionsBar
          editedCount={editedCount}
          submitting={submitting}
          onConfirm={() => handleSubmit(AgentActivityLogReviewStatus.Confirmed)}
          onReject={() => handleSubmit(AgentActivityLogReviewStatus.Rejected)}
        />
      </Box>
    </PageContainer>
  );
};
